// Affärsområden — sanningskälla för hur megamenyn fördelas.
//
// Varje affärsområde blir en egen toppnivåflik i huvudmenyn (SV + EN).
// Ett nytt område (t.ex. 3D) läggs till genom att skriva ett objekt här och
// sätta `enabled: true` — ingen ändring behövs i nav.ts, nav.en.ts,
// SiteHeader eller MobileMenu.

import type { MegaPanel } from "./nav";
import { primeServices } from "./prime-services";
import { fotoServices } from "./foto";

export type ServiceAreaId = "film" | "foto" | "3d";

export interface ServiceArea {
  id: ServiceAreaId;
  /** Sätt till false för områden som förbereds men inte ska synas i menyn än. */
  enabled: boolean;
  sv: MegaPanel;
  en: MegaPanel;
}

// Korta, konsekventa menyetiketter per språk (spegelvända SV/EN).
const primeNavLabels: Record<string, { sv: string; en: string }> = {
  "reklam-dokumentar": { sv: "Reklam & dok", en: "Ads & docs" },
  "film-streaming-tv": { sv: "Film & TV", en: "Film & TV" },
  "studio-live-webinars": { sv: "Studio & live", en: "Studio & live" },
  "regi-manus": { sv: "Regi & manus", en: "Directing & script" },
  "post-produktion": { sv: "Post", en: "Post" },
  poddar: { sv: "Podd", en: "Podcast" },
  "klipp-grade": { sv: "Klipp & grade", en: "Edit & grade" },
  ljussattning: { sv: "Ljus & gaffer", en: "Light & gaffer" },
};

const primeItems = primeServices.map((s) => ({
  to: "/prime/$slug",
  params: { slug: s.slug },
  label: primeNavLabels[s.slug]?.sv ?? s.shortTitle,
}));

const primeItemsEn = primeServices.map((s) => ({
  to: "/prime/$slug",
  params: { slug: s.slug },
  label: primeNavLabels[s.slug]?.en ?? s.shortTitle,
}));


const film: ServiceArea = {
  id: "film",
  enabled: true,
  sv: {
    kind: "mega",
    label: "Film",
    activeWhen: [
      "/tjanster",
      "/pulse",
      "/prime",
      "/pilot",
      "/process",
      "/varfor-film",
      "/heritage",
      "/brollopsfilm",
    ],
    columns: [
      {
        eyebrow: "Produkter",
        items: [
          { to: "/pulse", label: "Pulse", desc: "Filmabonnemang i marknadens takt" },
          { to: "/prime", label: "Prime", desc: "Skräddarsydda produktioner när det krävs mer" },
          { to: "/pilot", label: "Pilot", desc: "Lågtröskel-ingång — testa utan att binda er" },
          { to: "/heritage", label: "Heritage", desc: "Livsberättelser och kunskapsarv på film" },
          { to: "/brollopsfilm", label: "Bröllopsfilm", desc: "Cinematic & dokumentär — 1–3 per år" },
        ],
      },
      { eyebrow: "Prime", items: primeItems },
      {
        eyebrow: "Så jobbar vi",
        items: [
          { to: "/process", label: "Process", desc: "Från brief till leverans" },
          { to: "/varfor-film", label: "Varför film?", desc: "Affärsnyttan, kort" },
          { to: "/priser", label: "Priser", desc: "Transparenta paket" },
          { to: "/film-pris-kalkyl", label: "Priskalkyl", desc: "Uppskattning på 60 sekunder" },
          { to: "/tjanster", label: "Alla tjänster", desc: "Översikt och jämförelse" },
        ],
      },

    ],
    featured: {
      eyebrow: "Hjälp att välja",
      title: "Pulse eller Prime?",
      body: "Två produkter, två tempon. Se vilken som matchar er nästa fas.",
      to: "/tjanster",
      cta: "Jämför",
    },
  },
  en: {
    kind: "mega",
    label: "Film",
    activeWhen: [
      "/tjanster",
      "/pulse",
      "/prime",
      "/pilot",
      "/process",
      "/varfor-film",
      "/heritage",
      "/brollopsfilm",
    ],
    columns: [
      {
        eyebrow: "Products",
        items: [
          { to: "/pulse", label: "Pulse", desc: "Video subscription at market pace" },
          { to: "/prime", label: "Prime", desc: "Custom productions when you need more" },
          { to: "/pilot", label: "Pilot", desc: "A low-threshold way to try us out" },
          { to: "/heritage", label: "Heritage", desc: "Life stories and knowledge transfer on film" },
          { to: "/brollopsfilm", label: "Wedding film", desc: "Cinematic & documentary — 1–3 a year" },
        ],
      },
      { eyebrow: "Prime", items: primeItemsEn },
      {
        eyebrow: "How we work",
        items: [
          { to: "/process", label: "Process", desc: "From brief to delivery" },
          { to: "/varfor-film", label: "Why video?", desc: "The business case, short" },
          { to: "/priser", label: "Pricing", desc: "Transparent packages" },
          { to: "/film-pris-kalkyl", label: "Price calculator", desc: "An estimate in 60 seconds" },
          { to: "/tjanster", label: "All services", desc: "Overview and comparison" },
        ],
      },

    ],
    featured: {
      eyebrow: "Help me choose",
      title: "Pulse or Prime?",
      body: "Two products, two tempos. See which matches your next phase.",
      to: "/tjanster",
      cta: "Compare",
    },
  },
};

const fotoNavLabels: Record<string, { sv: string; en: string }> = {
  foretagsfotografering: { sv: "Företagsfoto", en: "Corporate photo" },
  foretagsportratt: { sv: "Porträtt", en: "Portraits" },
  kampanjfoto: { sv: "Kampanjfoto", en: "Campaign photo" },
  eventfoto: { sv: "Eventfoto", en: "Event photo" },
  produktfoto: { sv: "Produktfoto", en: "Product photo" },
  studiofotografering: { sv: "Studiofoto", en: "Studio photo" },
  "foto-och-film": { sv: "Foto + film", en: "Photo + film" },
};

const fotoBaseItems = fotoServices.filter((s) => s.slug !== "vad-kostar-en-fotograf");

const fotoNavItems = fotoBaseItems.map((s) => ({
  to: s.path,
  label: fotoNavLabels[s.slug]?.sv ?? s.nav,
  desc: s.desc,
}));

const fotoNavItemsEn = fotoBaseItems.map((s) => ({
  to: s.path,
  label: fotoNavLabels[s.slug]?.en ?? s.nav,
  desc: s.desc,
}));

const foto: ServiceArea = {
  id: "foto",
  enabled: false,
  sv: {
    kind: "mega",
    label: "Foto",
    activeWhen: ["/foto"],
    columns: [
      {
        eyebrow: "Tjänster",
        items: [
          ...fotoNavItems,
          { to: "/foto", label: "Allt om foto", desc: "Portfolio, process och offert" },
        ],
      },
      {
        eyebrow: "Special",
        items: [
          { to: "/foto/headshots", label: "Headshots", desc: "5–10 min per person, hela bolaget på en dag" },
          { to: "/foto/dronarfoto", label: "Drönarfoto", desc: "Flygbilder med certifierad pilot" },
          { to: "/foto/retusch", label: "Retusch", desc: "Nivåer, frilägg och vad som ingår" },
          { to: "/foto/bildbank", label: "Bildbank", desc: "Struktur, rättigheter och påfyllning" },
        ],
      },
      {
        eyebrow: "Pris",
        items: [
          {
            to: "/foto/vad-kostar-en-fotograf",
            label: "Vad kostar foto?",
            desc: "Kostnadsdrivare och offertlogik",
          },
          {
            to: "/foto/foretagsportratt-pris",
            label: "Pris: porträtt",
            desc: "Halvdag, heldag och kostnad per person",
          },
          {
            to: "/foto/produktfoto-pris",
            label: "Pris: produkt",
            desc: "Per artikel, vinklar och frilägg",
          },
          { to: "/foto/jamfor", label: "Jämför upplägg", desc: "Frilans, byrå eller abonnemang" },
        ],
      },
    ],
    featured: {
      eyebrow: "Fenny Foto",
      title: "Bilder med samma regi som filmen",
      body: "Porträtt, kampanj, produkt och event. Offert per uppdrag inom en arbetsdag.",
      to: "/foto",
      cta: "Se portfolion",
    },
  },
  en: {
    kind: "mega",
    label: "Photo",
    activeWhen: ["/foto"],
    columns: [
      {
        eyebrow: "Services",
        items: [
          ...fotoNavItemsEn,
          { to: "/foto", label: "All about photo", desc: "Portfolio, process and quotes" },
        ],
      },
      {
        eyebrow: "Specials",
        items: [
          { to: "/foto/headshots", label: "Headshots", desc: "5–10 minutes per person, whole company in a day" },
          { to: "/foto/dronarfoto", label: "Drone photo", desc: "Aerials with certified pilot" },
          { to: "/foto/retusch", label: "Retouching", desc: "Levels, cut-outs and what is included" },
          { to: "/foto/bildbank", label: "Image library", desc: "Structure, rights and upkeep" },
        ],
      },
      {
        eyebrow: "Pricing",
        items: [
          {
            to: "/foto/vad-kostar-en-fotograf",
            label: "What photo costs",
            desc: "Cost drivers and how we quote",
          },
          {
            to: "/foto/foretagsportratt-pris",
            label: "Price: portraits",
            desc: "Half day, full day and cost per person",
          },
          {
            to: "/foto/produktfoto-pris",
            label: "Price: product",
            desc: "Per item, angles and cut-outs",
          },
          { to: "/foto/jamfor", label: "Compare setups", desc: "Freelance, agency or retainer" },
        ],
      },
    ],


    featured: {
      eyebrow: "Fenny Photo",
      title: "Stills directed like our films",
      body: "Portraits, campaign, product and event. Quote per assignment within one working day.",
      to: "/foto",
      cta: "See the portfolio",
    },
  },
};

// Förberett affärsområde — sätt enabled: true när 3D-sidorna finns på plats
// (/3d, /3d/produktvisualisering, /3d/arkitekturvisualisering, /3d/animation).
const tredimensionellt: ServiceArea = {
  id: "3d",
  enabled: false,
  sv: {
    kind: "mega",
    label: "3D",
    activeWhen: ["/3d"],
    columns: [
      {
        eyebrow: "3D-tjänster",
        items: [
          { to: "/3d/produktvisualisering", label: "Produktvisualisering", desc: "Fotorealistiska produkter utan fotografering" },
          { to: "/3d/arkitekturvisualisering", label: "Arkitekturvisualisering", desc: "Byggnader och miljöer före spaden i marken" },
          { to: "/3d/animation", label: "3D-animation", desc: "Förklara det som inte går att filma" },
          { to: "/3d", label: "Allt om 3D", desc: "Översikt, process och offert" },
        ],
      },
    ],
    featured: {
      eyebrow: "Nytt",
      title: "3D hos Fenny",
      body: "Visualisering och animation med samma regi och leveransdisciplin som våra filmer.",
      to: "/3d",
      cta: "Läs mer",
    },
  },
  en: {
    kind: "mega",
    label: "3D",
    activeWhen: ["/3d"],
    columns: [
      {
        eyebrow: "3D services",
        items: [
          { to: "/3d/produktvisualisering", label: "Product visualisation", desc: "Photoreal products without a shoot" },
          { to: "/3d/arkitekturvisualisering", label: "Architectural visualisation", desc: "Buildings and spaces before they exist" },
          { to: "/3d/animation", label: "3D animation", desc: "Explain what cannot be filmed" },
          { to: "/3d", label: "All about 3D", desc: "Overview, process and quotes" },
        ],
      },
    ],
    featured: {
      eyebrow: "New",
      title: "3D at Fenny",
      body: "Visualisation and animation with the same direction and delivery discipline as our films.",
      to: "/3d",
      cta: "Read more",
    },
  },
};

export const serviceAreas: ServiceArea[] = [film, foto, tredimensionellt];

export function serviceAreaPanels(lang: "sv" | "en"): MegaPanel[] {
  return serviceAreas.filter((a) => a.enabled).map((a) => (lang === "en" ? a.en : a.sv));
}

/**
 * Affärsområden som är på väg men ännu inte har sidor.
 * Just nu döljs de helt i menyerna (3D ska inte synas förrän sidorna finns),
 * därför listas inga id:n i `SHOW_COMING_SOON`.
 */
const SHOW_COMING_SOON: ServiceAreaId[] = [];

export function comingSoonAreas(lang: "sv" | "en"): { id: ServiceAreaId; label: string; note: string }[] {
  return serviceAreas
    .filter((a) => !a.enabled && SHOW_COMING_SOON.includes(a.id))
    .map((a) => ({
      id: a.id,
      label: lang === "en" ? a.en.label : a.sv.label,
      note: lang === "en" ? "Coming soon" : "Snart",
    }));
}

/** Etiketter för de aktiva affärsområdena — används för att markera dem i menyerna. */
export function serviceAreaLabels(lang: "sv" | "en"): string[] {
  return serviceAreas
    .filter((a) => a.enabled)
    .map((a) => (lang === "en" ? a.en.label : a.sv.label));
}
